import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { updateUserUiDataSettings } from '@/features/domain/ui'
import { selectUserConfigurationWeekStartsOn } from '@/features/domain/user'
import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { PersonalizationFormValues } from '../PersonalizationForm/typings'
import { useTexts } from './useTexts'

export function usePersonalizationData() {
  const weekStartsOn = useSelector(selectUserConfigurationWeekStartsOn)
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const texts = useTexts()
  const isUnMounted = useIsUnmounted()

  const personalizationData = useMemo<PersonalizationFormValues>(
    () => ({
      weekStartsOn,
    }),
    [weekStartsOn],
  )

  const updateAccountData = useCallback(
    async (data: PersonalizationFormValues, onDiscard: () => void) => {
      try {
        const thunkResult = await dispatch(updateUserUiDataSettings(data))

        if (!updateUserUiDataSettings.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        toast.success(texts.updatePersonalizationSuccess, { autoClose: 5000 })

        if (isUnMounted()) return

        onDiscard()
      } catch (e) {
        toast.error(texts.updatePersonalizationError, { autoClose: 5000 })
      }
    },
    [dispatch, toast, texts, isUnMounted],
  )

  return [personalizationData, updateAccountData] as const
}
